/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { VEHICLE_STATUS } from "../../../../types/vehicle.type";
import "../../../../common/components/ui/table/Calendar.scss";
import "../../../../common/components/ui/table/TableFilters.scss";
import "../../CSS/DashboardVehicles.scss";
import { ArrowBigDown, ArrowBigUp } from "lucide-react";
import { LiaMotorcycleSolid } from "react-icons/lia";
import { useCompanyStore } from "../../../../common/states/store/Company";
import { IoClose, IoFilterSharp } from "react-icons/io5";
import { PartnerSelectModal } from "./../PartnerSelectModalProps";
import { useNavigate } from "react-router-dom";
import { FaEquals } from "react-icons/fa6";
import CardMgi from "./Cards/MGI";
import CardTmi from "./Cards/TMI";
import CardIRFMonthly from "./Cards/IRFMonthly";
import CardIRFWeekly from "./Cards/IRFWeekly";
import InsuranceUnprotected from "./Cards/InsuranceUnprotected";
import InsuranceProtected from "./Cards/InsuranceProtected";
import CardOperatingLoss from "./Cards/OperatingLoss";
import { ModalScoreOperation } from "../../ModalScoreOperation";
import { useAppStore } from "@/common/states/store";
import {
  DashboardFleetPanelStatus,
  useDashboardFleetPanel,
  useDashboardFleetPanelIRF,
  useDashboardFleetPanelStatuses,
} from "../../hooks";
import { LoadingCard } from "@/components/ui/loading-card";
import DelaysWithTreatments from "./Cards/DelaysWithTreatments";
import DelaysWithoutTreatments from "./Cards/DelaysWithoutTreatments";
import DelaysAvailable from "./Cards/DelaysAvailable";

interface VehiclesDashboardResult {
  statusVehicles?: Record<string, number>;
  statusDelayed?: Record<string, number>;
  totalVehicles?: number;
  noteOperation?: number;
  countRentetPlus?: number;
  [key: string]: any;
}

function getEnumKeyByLabel(label: string): string | undefined {
  if (label === "Manutenções não finalizadas")
    return "MAINTENANCE_NOT_COMPLETED";
  if (label === "Manutenções contestadas") return "MAINTENANCE_DISPUTED";
  return Object.keys(STATUS_LABELS).find(
    (key) => STATUS_LABELS[key as keyof typeof STATUS_LABELS] === label
  );
}

const LINKABLE_OPERATIONAL_MANAGEMENT_STATUS_KEYS = [
  VEHICLE_STATUS.AVAILABLE,
  VEHICLE_STATUS.UNDER_PREPARATION,
  VEHICLE_STATUS.IN_REPAIR,
  VEHICLE_STATUS.IN_REVISION,
  VEHICLE_STATUS.THIRD_PARTY_INSURANCE,
  VEHICLE_STATUS.INSURANCE,
  VEHICLE_STATUS.TRIGGER_OPENING,
  VEHICLE_STATUS.PREPARATION_FOR_SALE,
  VEHICLE_STATUS.MISAPPROPRIATION,
  VEHICLE_STATUS.TRIAGE,
  VEHICLE_STATUS.REPOSSESSION,
  VEHICLE_STATUS.IN_READY_RESPONSE,
];

const LINKABLE_VEHICLES_STATUS_KEYS = [
  VEHICLE_STATUS.RENTED,
  VEHICLE_STATUS.THEFT_ROBBERY,
  VEHICLE_STATUS.IN_TRANSIT,
  VEHICLE_STATUS.WAITING_PAYMENT,
  VEHICLE_STATUS.IMPOUNDED,
  VEHICLE_STATUS.SELLED,
  VEHICLE_STATUS.REIMBURSEMENT,
  VEHICLE_STATUS.AWAITING_TRANSFER,
];

const DEADLINE_STATUS = {
  [VEHICLE_STATUS.INSURANCE]: 2,
  [VEHICLE_STATUS.UNDER_PREPARATION]: 3,
  [VEHICLE_STATUS.IN_REVISION]: 3,
  [VEHICLE_STATUS.IN_REPAIR]: 7,
  [VEHICLE_STATUS.AVAILABLE]: 8,
  [VEHICLE_STATUS.IMPOUNDED]: 10,
  [VEHICLE_STATUS.THEFT_ROBBERY]: 10,
  [VEHICLE_STATUS.MISAPPROPRIATION]: 60,
  [VEHICLE_STATUS.IN_TRANSIT]: 3,
  [VEHICLE_STATUS.PREPARATION_FOR_SALE]: 15,
  [VEHICLE_STATUS.TRIAGE]: 1,
  [VEHICLE_STATUS.IN_READY_RESPONSE]: 2,
};

const STATUS_LABELS = {
  [VEHICLE_STATUS.AVAILABLE]: "Disponível",
  [VEHICLE_STATUS.RENTED]: "Locado",
  [VEHICLE_STATUS.UNDER_PREPARATION]: "Em preparação",
  [VEHICLE_STATUS.IN_REPAIR]: "Em manutenção",
  [VEHICLE_STATUS.IN_REVISION]: "Reparo Estético",
  [VEHICLE_STATUS.IN_TRANSIT]: "Em trânsito",
  [VEHICLE_STATUS.WAITING_PAYMENT]: "Aguardando pagamento",
  [VEHICLE_STATUS.THIRD_PARTY_INSURANCE]: "Acionamento Terceiros",
  [VEHICLE_STATUS.INSURANCE]: "Abertura de acionamento",
  [VEHICLE_STATUS.TRIGGER_OPENING]: "Proteção acionada",
  [VEHICLE_STATUS.IMPOUNDED]: "Apreendido",
  [VEHICLE_STATUS.PREPARATION_FOR_SALE]: "Motos para Venda",
  [VEHICLE_STATUS.SELLED]: "Vendido",
  [VEHICLE_STATUS.THEFT_ROBBERY]: "Furto/Roubo",
  [VEHICLE_STATUS.MISAPPROPRIATION]: "Apropriação indébita",
  [VEHICLE_STATUS.REIMBURSEMENT]: "Aguard. Ressarcimento",
  [VEHICLE_STATUS.REPOSSESSION]: "Recolhimento",
  [VEHICLE_STATUS.TRIAGE]: "Triagem",
  [VEHICLE_STATUS.IN_READY_RESPONSE]: "Em pronta resposta",
  [VEHICLE_STATUS.AWAITING_TRANSFER]: "Vendido/Aguardando Transferência",
};

export default function RenderDashboardVehicles() {
  const { selectedCompanies, selectedUf, filterMode } = useCompanyStore(
    (state) => ({
      selectedCompanies: state.selectedCompanies,
      selectedUf: state.selectedUf,
      filterMode: state.filterMode,
    })
  );
  const [, setTotalVehicles] = useState(0);
  const [vehiclesByStatus, setVehiclesByStatus] = useState<
    Record<string, number>
  >({});
  const [animateIcon, setAnimateIcon] = useState(false);
  const buttonPopupRef = useRef<HTMLButtonElement>(null);
  const [partnerSelectOpen, setPartnerSelectOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<
    { value: string; label: string }[] | null
  >(null);
  const [statusDelayed, setStatusDelayed] = useState<Record<string, any>>({});
  const [noteOperation, setNoteOperation] = useState<number | null>(null);
  const [comparison, setComparison] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [displayCompanyName, setDisplayCompanyName] =
    useState("OPERAÇÃO BRASIL");
  const [insuranceAndSellCounts, setInsuranceAndSellCounts] = useState<{
    protectedAndSelled: number;
    unprotectedAndNotSelled: number;
  }>({ protectedAndSelled: 0, unprotectedAndNotSelled: 0 });
  const [operatingLossTotal, setOperatingLossTotal] = useState(0);
  const [operatingLossDaily, setOperatingLossDaily] = useState(0);

  const {
    dashboard: {
      fleet: { setOpenModalScoreOperation },
    },
  } = useAppStore();
  const {
    dashboard: {
      fleet: {
        modals: { scoreOperation: scoreOperationOpen },
      },
    },
  } = useAppStore();

  const handleOpenModalScopeOperation = () => {
    setOpenModalScoreOperation();
  };

  const { data: dataIRF, isFetching: loadingIRF } =
    useDashboardFleetPanelIRF(selectedPartner);
  const { data: dataStatuses, isFetching: loadingStatuses } =
    useDashboardFleetPanelStatuses(selectedPartner);
  const { data, isFetching: loading } = useDashboardFleetPanel(selectedPartner);

  useEffect(() => {
    if (filterMode === "all") {
      setDisplayCompanyName("OPERAÇÃO BRASIL");
    } else if (filterMode === "uf" && selectedUf) {
      setDisplayCompanyName(selectedUf);
    } else if (filterMode === "cities" && selectedCompanies.length > 0) {
      if (selectedCompanies.length === 1) {
        const name = selectedCompanies[0].companyName?.trim();
        setDisplayCompanyName(name ?? "OPERAÇÃO BRASIL");
      } else {
        const names = selectedCompanies
          .map((c) => c.companyName.trim())
          .join(", ");
        setDisplayCompanyName(names);
      }
    } else {
      setDisplayCompanyName("OPERAÇÃO BRASIL");
    }
  }, [filterMode, selectedUf, selectedCompanies]);

  const handlePartnerSelect = async (
    partners: { value: string; label: string }[] | null
  ) => {
    setSelectedPartner(partners);
  };

  const containerRef = useRef<HTMLDivElement>(null);

  const handleEnterFullscreen = () => {
    const el = containerRef.current;
    if (el && el.requestFullscreen) {
      el.requestFullscreen();
    } else if (el && (el as any).webkitRequestFullscreen) {
      (el as any).webkitRequestFullscreen();
    } else if (el && (el as any).msRequestFullscreen) {
      (el as any).msRequestFullscreen();
    }
  };

  const getStatusVehicles = (
    statusData: Record<string, number>,
    resultData: VehiclesDashboardResult
  ) => {
    const statusVehicles: Record<string, number> = {};

    Object.entries(statusData || {}).forEach(([status, count]) => {
      if (STATUS_LABELS[status]) {
        statusVehicles[STATUS_LABELS[status]] = Number(count) || 0;
      }
    });

    const disponiveis = Number(statusData?.["available"] || 0);
    const emPreparacao = Number(statusData?.["under_preparation"] || 0);
    const emManutencao = Number(statusData?.["in_repair"] || 0);
    const reparoEstetico = Number(statusData?.["in_revision"] || 0);
    const vendidos = Number(statusData?.["selled"] || 0);
    const emApropriacaoEmDebito = Number(statusData?.["misappropriation"] || 0);
    const locados = Number(statusData?.["rented"] || 0);
    const awaitingTransfer = Number(statusData?.["awaiting_transfer"] || 0);

    const totalVeiculos = Number(dataStatuses?.["totalVehicles"] || 0);
    const totalAtivos = totalVeiculos - vendidos;

    let txOcupacao = 0;
    let txDesocupacao = 0;
    let txInoperancia = 0;

    if (totalAtivos > 0) {
      txOcupacao = parseFloat(
        ((1 - disponiveis / totalAtivos) * 100).toFixed(2)
      );
      txOcupacao = isNaN(txOcupacao) ? 0 : txOcupacao;

      txDesocupacao = parseFloat((100 - txOcupacao).toFixed(2));
      txInoperancia = parseFloat(
        (
          ((emPreparacao + emManutencao + reparoEstetico) / totalAtivos) *
          100
        ).toFixed(2)
      );
    }

    const margemIdealMgi = 0.15;
    const mgiBruto =
      (margemIdealMgi - (txInoperancia / 100 + txDesocupacao / 100)) *
      totalAtivos;
    const mgi = mgiBruto;

    statusVehicles["Total de Veículos"] = totalVeiculos;
    statusVehicles["Veículos ativos"] = totalAtivos;
    statusVehicles["Tx Ocupação"] = txOcupacao;
    statusVehicles["Tx Desocupação"] = txDesocupacao;
    statusVehicles["Tx Inoperância"] = txInoperancia;
    statusVehicles["Aguardando Transferência"] = awaitingTransfer;
    statusVehicles["% Plus"] = parseFloat(
      (
        (Number(resultData["countRentetPlus"] || 0) / (locados || 1)) *
        100
      ).toFixed(2)
    );
    statusVehicles["Apropriação indébita"] = emApropriacaoEmDebito;
    statusVehicles["PML"] = parseFloat(
      (
        (Number(resultData["totalOwnerVehicles"] || 0) / totalVeiculos) *
        100
      ).toFixed(2)
    );
    statusVehicles["M.G.I"] = parseFloat(mgi.toFixed(2));
    statusVehicles["TMI"] = resultData["totalDaysInBase"] || 0;

    return statusVehicles;
  };

  const getOperatingLoss = (
    statusData: Record<string, number>,
    totalDaysInBase: number
  ) => {
    const disponiveis = Number(statusData?.["available"] || 0);
    const emPreparacao = Number(statusData?.["under_preparation"] || 0);
    const emManutencao = Number(statusData?.["in_repair"] || 0);
    const reparoEstetico = Number(statusData?.["in_revision"] || 0);

    const totalLoss =
      (emPreparacao + disponiveis + emManutencao + reparoEstetico) *
      totalDaysInBase *
      44.9;
    const dailyLoss =
      (emPreparacao + disponiveis + emManutencao + reparoEstetico) * 44.9;

    return { totalLoss, dailyLoss };
  };

  const fetchData = useCallback(async () => {
    const result: VehiclesDashboardResult = data as VehiclesDashboardResult;
    const statusData = dataStatuses ?? ({} as DashboardFleetPanelStatus);

    setStatusDelayed(statusData.statusDelayed ?? {});
    setTotalVehicles(Number(statusData.totalVehicles ?? 0));

    const vehiclesStatus = getStatusVehicles(statusData.statusVehicles, result);
    setVehiclesByStatus(vehiclesStatus);

    const losses = getOperatingLoss(
      statusData.statusVehicles,
      result.totalDaysInBase ?? 0
    );
    setOperatingLossTotal(losses.totalLoss);
    setOperatingLossDaily(losses.dailyLoss);

    setComparison(result.comparison ?? null);
    setNoteOperation(result.noteOperation ?? 0);
    setInsuranceAndSellCounts(
      result.insuranceAndSellCounts ?? {
        protectedAndSelled: 0,
        unprotectedAndNotSelled: 0,
      }
    );
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, dataStatuses]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  function formatStyleNoteOperation(note: number | null): React.CSSProperties {
    if (note === null) return {};
    if (note >= 7.5 && note < 8.9) return { color: "maroon" };
    if (note >= 9 && note < 9.5) return { color: "green" };
    if (note >= 9.5) return { color: "blue" };
    return { color: "red" };
  }

  return (
    <div ref={containerRef} className="p-6 bg-gray-50 min-h-screen bg-white">
      <div className="flex flex-row sp mb-8 border-gray-200 pb-4">
        <div className="flex flex-col justify-center items-start">
          <h1 className="text-2xl font-semibold flex items-center gap-2 text-black">
            <LiaMotorcycleSolid
              onClick={handleEnterFullscreen}
              className="w-8 h-8 text-redPrimary hidden md:block"
            />
            Dashboard Frota - {displayCompanyName}
          </h1>
        </div>
        <div className="ml-auto flex items-center">
          <button
            id="filterButton"
            onClick={() => {
              setPartnerSelectOpen(true);
              setAnimateIcon(true);
            }}
            ref={buttonPopupRef}
            aria-placeholder="filtros adicionais"
            className="relative w-10 h-10 flex items-center justify-center"
          >
            {!animateIcon ? (
              <div
                className={`rotate-icon ${
                  animateIcon ? "rotate-filter" : "stop-filter"
                }`}
              >
                <IoFilterSharp />
              </div>
            ) : (
              <div
                className={`rotate-icon ${
                  animateIcon ? "stop-filter" : "rotate-filter"
                }`}
              >
                <IoClose />
              </div>
            )}
          </button>

          <PartnerSelectModal
            isOpen={partnerSelectOpen}
            onClose={() => {
              setPartnerSelectOpen(false);
              setAnimateIcon(false);
            }}
            onSelectPartner={handlePartnerSelect}
            selectedPartner={selectedPartner}
          />
        </div>
        {!loading && (
          <div
            className="mb-4 flex items-center text-center cursor-pointer"
            style={formatStyleNoteOperation(noteOperation)}
            onClick={handleOpenModalScopeOperation}
          >
            <div
              className="relative bg-white flex flex-col justify-center items-center border border-gray-200 shadow-sm rounded-xl px-4 py-2"
              style={{ minWidth: 150 }}
            >
              <div className="w-full flex items-center text-center mb-1">
                {comparison?.["noteOperation"] < 0 ? (
                  <ArrowBigDown
                    className={`w-6 h-6 fill-current stroke-none text-red-600`}
                  />
                ) : (
                  <ArrowBigUp
                    className={`w-6 h-6 fill-current stroke-none text-green-600`}
                  />
                )}
                <h3 className="text-md font-bold text-gray-600">
                  Nota Operação
                </h3>
              </div>
              <p
                className="text-lg font-bold text-black"
                style={formatStyleNoteOperation(noteOperation)}
              >
                {noteOperation?.toFixed(2) ?? 0}
              </p>
            </div>
          </div>
        )}
      </div>

      <div
        className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-6 mb-6"
        role="resumo-frota"
      >
        <LoadingCard loading={loading}>
          <CardTmi
            value={vehiclesByStatus["TMI"]}
            delta={comparison?.["tmi"] ?? 0}
          />
        </LoadingCard>
        <LoadingCard loading={loadingStatuses}>
          <DelaysWithoutTreatments
            value={statusDelayed["delays-without-treatments"] ?? 0}
            meta={
              typeof statusDelayed["metaWithout"] === "object" &&
              statusDelayed["metaWithout"] !== null
                ? (statusDelayed["metaWithout"] as any)
                : {}
            }
          />
        </LoadingCard>
        <LoadingCard loading={loadingStatuses}>
          <DelaysAvailable
            value={
              Object.prototype.hasOwnProperty.call(
                statusDelayed,
                "metaAvailable"
              )
                ? statusDelayed["metaAvailable"]?.count
                : 0
            }
            meta={
              typeof statusDelayed["metaAvailable"] === "object" &&
              statusDelayed["metaAvailable"] !== null
                ? (statusDelayed["metaAvailable"] as any)
                : {}
            }
          />
        </LoadingCard>
        <LoadingCard loading={loadingStatuses}>
          <DelaysWithTreatments
            value={statusDelayed["delays-with-treatments"] ?? 0}
            meta={
              typeof statusDelayed["metaWith"] === "object" &&
              statusDelayed["metaWith"] !== null
                ? (statusDelayed["metaWith"] as any)
                : {}
            }
          />
        </LoadingCard>
        <LoadingCard loading={loading}>
          <CardOperatingLoss
            totalValue={operatingLossTotal}
            dailyValue={operatingLossDaily}
          />
        </LoadingCard>
        <LoadingCard loading={loadingStatuses}>
          <Card
            key="Manutenções contestadas"
            id="card-contested-maintenances"
            title="Manutenções contestadas"
            value={dataStatuses?.contestedMaintenances ?? 0}
            delayed={
              dataStatuses?.contestedMaintenances &&
              dataStatuses?.contestedMaintenances > 0
                ? true
                : false
            }
            totalDelayed={0}
            delta={0}
            showDeltaArrow
          />
        </LoadingCard>
        <LoadingCard loading={loadingStatuses}>
          <Card
            key="Manutenções não finalizadas"
            id="card-unfinished-maintenances"
            title="Manutenções não finalizadas"
            value={dataStatuses?.unfinishedMaintenances ?? 0}
            delayed={
              dataStatuses?.unfinishedMaintenances &&
              dataStatuses?.unfinishedMaintenances > 0
                ? true
                : false
            }
            totalDelayed={0}
            delta={0}
            showDeltaArrow
          />
        </LoadingCard>

        <LoadingCard loading={loadingStatuses}>
          <Card
            key="Tx Ocupação"
            id="card-rate-occupation"
            title="Tx Ocupação"
            value={vehiclesByStatus["Tx Ocupação"] ?? 0}
            delayed={false}
            totalDelayed={0}
            delta={1}
            showDeltaArrow
          />
        </LoadingCard>
        <LoadingCard loading={loadingStatuses}>
          <Card
            key="Tx Desocupação"
            id="card-rate-unoccupation"
            title="Tx Desocupação"
            value={vehiclesByStatus["Tx Desocupação"] ?? 0}
            delayed={false}
            totalDelayed={0}
            delta={1}
            showDeltaArrow
          />
        </LoadingCard>
        <LoadingCard loading={loadingStatuses}>
          <Card
            key="Tx Inoperância"
            id="card-rate-inoperancy"
            title="Tx Inoperância"
            value={vehiclesByStatus["Tx Inoperância"] ?? 0}
            delayed={false}
            totalDelayed={0}
            delta={1}
            showDeltaArrow
          />
        </LoadingCard>
        <LoadingCard loading={loadingStatuses}>
          <Card
            key="PML"
            id="card-pml"
            title="PML"
            value={vehiclesByStatus["PML"] ?? 0}
            delayed={false}
            totalDelayed={0}
            delta={1}
            showDeltaArrow
          />
        </LoadingCard>
        <LoadingCard loading={loadingStatuses}>
          <Card
            key="% Plus"
            id="card-rate-plus"
            title="% Plus"
            value={vehiclesByStatus["% Plus"] ?? 0}
            delayed={false}
            totalDelayed={0}
            delta={1}
            showDeltaArrow
          />
        </LoadingCard>
        <LoadingCard loading={loadingStatuses}>
          <CardMgi value={vehiclesByStatus["M.G.I"]} />
        </LoadingCard>
        <LoadingCard loading={loading}>
          <Card
            key="Total de Veículos"
            id="card-total-vehicles"
            title="Total de Veículos"
            value={vehiclesByStatus["Total de Veículos"] ?? 0}
            delayed={false}
            totalDelayed={0}
            delta={1}
            showDeltaArrow
          />
        </LoadingCard>
        <LoadingCard loading={loadingStatuses}>
          <Card
            key="Veículos Ativos"
            id="card-active-vehicles"
            title="Veículos ativos"
            value={vehiclesByStatus["Veículos ativos"] ?? 0}
            delayed={false}
            totalDelayed={0}
            delta={1}
            showDeltaArrow
          />
        </LoadingCard>
      </div>
      <hr className="my-8 border-t border-gray-300" />
      <div
        className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6"
        role="resumo-frota"
      >
        <LoadingCard loading={loadingStatuses}>
          <Card
            key="Em preparação"
            id="card-under-preparation"
            title="Em preparação"
            value={vehiclesByStatus["Em preparação"] ?? 0}
            delayed={Object.prototype.hasOwnProperty.call(
              statusDelayed,
              "under_preparation"
            )}
            totalDelayed={
              Object.prototype.hasOwnProperty.call(
                statusDelayed,
                "under_preparation"
              )
                ? statusDelayed["under_preparation"]
                : 0
            }
            delta={comparison?.["under_preparation"]?.total?.delta ?? 0}
            showDeltaArrow
            percentage={
              ((vehiclesByStatus["Em preparação"] ?? 0) /
                (vehiclesByStatus["Veículos ativos"] ?? 1)) *
              100
            }
          />
        </LoadingCard>
        <LoadingCard loading={loadingStatuses}>
          <Card
            key="Disponível"
            id="card-available"
            title="Disponível"
            value={vehiclesByStatus["Disponível"] ?? 0}
            delayed={Object.prototype.hasOwnProperty.call(
              statusDelayed,
              "available"
            )}
            totalDelayed={
              Object.prototype.hasOwnProperty.call(statusDelayed, "available")
                ? statusDelayed["available"]
                : 0
            }
            delta={comparison?.["available"]?.total?.delta ?? 0}
            showDeltaArrow
            percentage={
              ((vehiclesByStatus["Disponível"] ?? 0) /
                (vehiclesByStatus["Veículos ativos"] ?? 1)) *
              100
            }
          />
        </LoadingCard>
        <LoadingCard loading={loadingStatuses}>
          <Card
            key="Locado"
            id="card-rented"
            title="Locado"
            value={vehiclesByStatus["Locado"] ?? 0}
            delayed={false}
            totalDelayed={0}
            delta={comparison?.["rented"]?.total?.delta ?? 0}
            showDeltaArrow
            percentage={
              ((vehiclesByStatus["Locado"] ?? 0) /
                (vehiclesByStatus["Veículos ativos"] ?? 1)) *
              100
            }
          />
        </LoadingCard>
        <LoadingCard loading={loadingStatuses}>
          <Card
            key="Recolhimento"
            id="card-repossession"
            title="Recolhimento"
            value={vehiclesByStatus["Recolhimento"] ?? 0}
            delayed={Object.prototype.hasOwnProperty.call(
              statusDelayed,
              "repossession"
            )}
            totalDelayed={
              Object.prototype.hasOwnProperty.call(
                statusDelayed,
                "repossession"
              )
                ? statusDelayed["repossession"]
                : 0
            }
            delta={comparison?.["repossession"]?.total?.delta ?? 0}
            showDeltaArrow
            percentage={
              ((vehiclesByStatus["Recolhimento"] ?? 0) /
                (vehiclesByStatus["Veículos ativos"] ?? 1)) *
              100
            }
          />
        </LoadingCard>
        <LoadingCard loading={loadingStatuses}>
          <Card
            key="Triagem"
            id="card-triage"
            title="Triagem"
            value={vehiclesByStatus["Triagem"] ?? 0}
            delayed={Object.prototype.hasOwnProperty.call(
              statusDelayed,
              "triage"
            )}
            totalDelayed={
              Object.prototype.hasOwnProperty.call(statusDelayed, "triage")
                ? statusDelayed["triage"]
                : 0
            }
            delta={comparison?.["triage"]?.total?.delta ?? 0}
            showDeltaArrow
            percentage={
              ((vehiclesByStatus["Triagem"] ?? 0) /
                (vehiclesByStatus["Veículos ativos"] ?? 1)) *
              100
            }
          />
        </LoadingCard>
        <LoadingCard loading={loadingStatuses}>
          <Card
            key="Reparo Estético"
            id="card-in-revision"
            title="Reparo Estético"
            value={vehiclesByStatus["Reparo Estético"] ?? 0}
            delayed={Object.prototype.hasOwnProperty.call(
              statusDelayed,
              "in_revision"
            )}
            totalDelayed={
              Object.prototype.hasOwnProperty.call(statusDelayed, "in_revision")
                ? statusDelayed["in_revision"]
                : 0
            }
            delta={comparison?.["in_revision"]?.total?.delta ?? 0}
            showDeltaArrow
            percentage={
              ((vehiclesByStatus["Reparo Estético"] ?? 0) /
                (vehiclesByStatus["Veículos ativos"] ?? 1)) *
              100
            }
          />
        </LoadingCard>
        <LoadingCard loading={loadingStatuses}>
          <Card
            key="Em manutenção"
            id="card-under-maintenance"
            title="Em manutenção"
            value={vehiclesByStatus["Em manutenção"] ?? 0}
            delayed={Object.prototype.hasOwnProperty.call(
              statusDelayed,
              "in_repair"
            )}
            totalDelayed={
              Object.prototype.hasOwnProperty.call(statusDelayed, "in_repair")
                ? statusDelayed["in_repair"]
                : 0
            }
            delta={comparison?.["in_repair"]?.total?.delta ?? 0}
            showDeltaArrow
            percentage={
              ((vehiclesByStatus["Em manutenção"] ?? 0) /
                (vehiclesByStatus["Veículos ativos"] ?? 1)) *
              100
            }
          />
        </LoadingCard>
        <LoadingCard loading={loadingStatuses}>
          <Card
            key="Acionamento Terceiros"
            id="card-third-party-activation"
            title="Acionamento Terceiros"
            value={vehiclesByStatus["Acionamento Terceiros"] ?? 0}
            delayed={Object.prototype.hasOwnProperty.call(
              statusDelayed,
              "third_party_activation"
            )}
            totalDelayed={
              Object.prototype.hasOwnProperty.call(
                statusDelayed,
                "third_party_activation"
              )
                ? statusDelayed["third_party_activation"]
                : 0
            }
            delta={comparison?.["third_party_activation"]?.total?.delta ?? 0}
            showDeltaArrow
            percentage={
              ((vehiclesByStatus["Acionamento Terceiros"] ?? 0) /
                (vehiclesByStatus["Veículos ativos"] ?? 1)) *
              100
            }
          />
        </LoadingCard>
        <LoadingCard loading={loadingStatuses}>
          <Card
            key="Aguardando pagamento"
            id="card-waiting-payment"
            title="Aguardando pagamento"
            value={vehiclesByStatus["Aguardando pagamento"] ?? 0}
            delayed={Object.prototype.hasOwnProperty.call(
              statusDelayed,
              "waiting_payment"
            )}
            totalDelayed={
              Object.prototype.hasOwnProperty.call(
                statusDelayed,
                "waiting_payment"
              )
                ? statusDelayed["waiting_payment"]
                : 0
            }
            delta={comparison?.["waiting_payment"]?.total?.delta ?? 0}
            showDeltaArrow
            percentage={
              ((vehiclesByStatus["Aguardando pagamento"] ?? 0) /
                (vehiclesByStatus["Veículos ativos"] ?? 1)) *
              100
            }
          />
        </LoadingCard>
        <LoadingCard loading={loadingStatuses}>
          <Card
            key="Em trânsito"
            id="card-in-transit"
            title="Em trânsito"
            value={vehiclesByStatus["Em trânsito"] ?? 0}
            delayed={Object.prototype.hasOwnProperty.call(
              statusDelayed,
              "in_transit"
            )}
            totalDelayed={
              Object.prototype.hasOwnProperty.call(statusDelayed, "in_transit")
                ? statusDelayed["in_transit"]
                : 0
            }
            delta={comparison?.["in_transit"]?.total?.delta ?? 0}
            showDeltaArrow
            percentage={
              ((vehiclesByStatus["Em trânsito"] ?? 0) /
                (vehiclesByStatus["Veículos ativos"] ?? 1)) *
              100
            }
          />
        </LoadingCard>
        <LoadingCard loading={loadingStatuses}>
          <Card
            key="Em pronta resposta"
            id="card-in-ready-response"
            title="Em pronta resposta"
            value={vehiclesByStatus["Em pronta resposta"] ?? 0}
            delayed={Object.prototype.hasOwnProperty.call(
              statusDelayed,
              "in_ready_response"
            )}
            totalDelayed={
              Object.prototype.hasOwnProperty.call(
                statusDelayed,
                "in_ready_response"
              )
                ? statusDelayed["in_ready_response"]
                : 0
            }
            delta={comparison?.["in_ready_response"]?.total?.delta ?? 0}
            showDeltaArrow
            percentage={
              ((vehiclesByStatus["Em pronta resposta"] ?? 0) /
                (vehiclesByStatus["Veículos ativos"] ?? 1)) *
              100
            }
            className={
              Number(statusDelayed?.in_ready_response ?? 0) > 0
                ? "bg-red-500 text-white"
                : Number(vehiclesByStatus["Apreendido"] ?? 0) > 0
                ? "bg-red-500 text-black"
                : ""
            }
          />
        </LoadingCard>
        <LoadingCard loading={loadingStatuses}>
          <Card
            key="Apreendido"
            id="card-impounded"
            title="Apreendido"
            value={vehiclesByStatus["Apreendido"] ?? 0}
            delayed={Object.prototype.hasOwnProperty.call(
              statusDelayed,
              "impounded"
            )}
            totalDelayed={
              Object.prototype.hasOwnProperty.call(statusDelayed, "impounded")
                ? statusDelayed["impounded"]
                : 0
            }
            delta={comparison?.["impounded"]?.total?.delta ?? 0}
            showDeltaArrow
            percentage={
              ((vehiclesByStatus["Apreendido"] ?? 0) /
                (vehiclesByStatus["Veículos ativos"] ?? 1)) *
              100
            }
            className={
              Number(statusDelayed?.impounded ?? 0) > 0
                ? "bg-red-500 text-black"
                : Number(vehiclesByStatus["Apreendido"] ?? 0) > 0
                ? "bg-red-500 text-black"
                : ""
            }
          />
        </LoadingCard>
        <LoadingCard loading={loadingStatuses}>
          <Card
            key="Apropriação indébita"
            id="card-misappropriation"
            title="Apropriação indébita"
            value={vehiclesByStatus["Apropriação indébita"] ?? 0}
            delayed={Object.prototype.hasOwnProperty.call(
              statusDelayed,
              "misappropriation"
            )}
            totalDelayed={
              Object.prototype.hasOwnProperty.call(
                statusDelayed,
                "misappropriation"
              )
                ? statusDelayed["misappropriation"]
                : 0
            }
            delta={comparison?.["misappropriation"]?.total?.delta ?? 0}
            showDeltaArrow
            percentage={
              ((vehiclesByStatus["Apropriação indébita"] ?? 0) /
                (vehiclesByStatus["Veículos ativos"] ?? 1)) *
              100
            }
            className={
              Number(statusDelayed?.misappropriation ?? 0) > 0
                ? "bg-red-500 text-white"
                : Number(vehiclesByStatus["Apropriação indébita"] ?? 0) > 0
                ? "bg-red-500 text-black"
                : ""
            }
          />
        </LoadingCard>
        <LoadingCard loading={loadingStatuses}>
          <Card
            key="Motos para Venda"
            id="card-preparation_for_sale"
            title="Motos para Venda"
            value={vehiclesByStatus["Motos para Venda"] ?? 0}
            delayed={Object.prototype.hasOwnProperty.call(
              statusDelayed,
              "preparation_for_sale"
            )}
            totalDelayed={
              Object.prototype.hasOwnProperty.call(
                statusDelayed,
                "preparation_for_sale"
              )
                ? statusDelayed["preparation_for_sale"]
                : 0
            }
            delta={comparison?.["preparation_for_sale"]?.total?.delta ?? 0}
            showDeltaArrow
            percentage={
              ((vehiclesByStatus["Motos para Venda"] ?? 0) /
                (vehiclesByStatus["Veículos ativos"] ?? 1)) *
              100
            }
          />
        </LoadingCard>
        <LoadingCard loading={loadingStatuses}>
          <Card
            key="Vendido"
            id="card-selled-vehicles"
            title="Vendido"
            value={vehiclesByStatus["Vendido"] ?? 0}
            delayed={false}
            totalDelayed={0}
            delta={1}
            showDeltaArrow
          />
        </LoadingCard>
        <LoadingCard loading={loadingStatuses}>
          <Card
            key="Transferencia"
            id="card-awaiting-transfer"
            title="Vendido/Aguardando Transferência"
            value={vehiclesByStatus["Aguardando Transferência"] ?? 0}
            delayed={false}
            totalDelayed={0}
            delta={1}
            showDeltaArrow
          />
        </LoadingCard>
        <LoadingCard loading={loadingStatuses}>
          <Card
            key="Abertura de acionamento"
            id="card-insurance"
            title="Abertura de acionamento"
            value={vehiclesByStatus["Abertura de acionamento"] ?? 0}
            delayed={Object.prototype.hasOwnProperty.call(
              statusDelayed,
              "insurance"
            )}
            totalDelayed={
              Object.prototype.hasOwnProperty.call(statusDelayed, "insurance")
                ? statusDelayed["insurance"]
                : 0
            }
            delta={comparison?.["insurance"]?.total?.delta ?? 0}
            showDeltaArrow
            percentage={
              ((vehiclesByStatus["Abertura de acionamento"] ?? 0) /
                (vehiclesByStatus["Veículos ativos"] ?? 1)) *
              100
            }
          />
        </LoadingCard>
        <LoadingCard loading={loadingStatuses}>
          <Card
            key="Proteção acionada"
            id="card-trigger-opening"
            title="Proteção acionada"
            value={vehiclesByStatus["Proteção acionada"] ?? 0}
            delayed={Object.prototype.hasOwnProperty.call(
              statusDelayed,
              "trigger_opening"
            )}
            totalDelayed={
              Object.prototype.hasOwnProperty.call(
                statusDelayed,
                "trigger_opening"
              )
                ? statusDelayed["trigger_opening"]
                : 0
            }
            delta={comparison?.["trigger_opening"]?.total?.delta ?? 0}
            showDeltaArrow
            percentage={
              ((vehiclesByStatus["Proteção acionada"] ?? 0) /
                (vehiclesByStatus["Veículos ativos"] ?? 1)) *
              100
            }
          />
        </LoadingCard>
        <LoadingCard loading={loadingStatuses}>
          <Card
            key="Aguard. Ressarcimento"
            id="card-reimbursement"
            title="Aguard. Ressarcimento"
            value={vehiclesByStatus["Ressarcimento"] ?? 0}
            delayed={Object.prototype.hasOwnProperty.call(
              statusDelayed,
              "reimbursement"
            )}
            totalDelayed={
              Object.prototype.hasOwnProperty.call(
                statusDelayed,
                "reimbursement"
              )
                ? statusDelayed["reimbursement"]
                : 0
            }
            delta={comparison?.["reimbursement"]?.total?.delta ?? 0}
            showDeltaArrow
            percentage={
              ((vehiclesByStatus["Ressarcimento"] ?? 0) /
                (vehiclesByStatus["Veículos ativos"] ?? 1)) *
              100
            }
          />
        </LoadingCard>
        <InsuranceUnprotected
          value={insuranceAndSellCounts.unprotectedAndNotSelled ?? 0}
        />
        <InsuranceProtected
          value={insuranceAndSellCounts.protectedAndSelled ?? 0}
        />
        <LoadingCard loading={loadingIRF}>
          <CardIRFMonthly
            label={`1 / ${dataIRF?.IRF.monthly.indicator || 0}`}
            value={Number(dataIRF?.IRF.monthly.indicator || 0)}
          />
        </LoadingCard>

        <LoadingCard loading={loadingIRF}>
          <CardIRFWeekly
            label={`1 / ${dataIRF?.IRF.weekly.indicator || 0}`}
            value={Number(dataIRF?.IRF.weekly.indicator || 0)}
          />
        </LoadingCard>
      </div>
      {scoreOperationOpen && <ModalScoreOperation />}
    </div>
  );

  function Card({
    title,
    id,
    value,
    icon,
    delayed = false,
    totalDelayed = 0,
    delta = 0,
    percentage = 0,
    showDeltaArrow = false,
    className = "",
  }: {
    title: string;
    value: number;
    id?: string;
    icon?: React.ReactNode;
    delayed?: boolean;
    totalDelayed?: number;
    delta?: number;
    percentage?: number;
    showDeltaArrow?: boolean;
    className?: string;
  }) {
    const navigate = useNavigate();
    const isTaxa =
      title.includes("Tx") || title.includes("%") || title.includes("PML");
    const safeValue = typeof value === "number" && !isNaN(value) ? value : 0;
    const displayValue = isTaxa
      ? `${safeValue.toFixed(2).replace(".", ",")}%`
      : safeValue.toLocaleString("pt-BR");

    const enumKey = getEnumKeyByLabel(title);
    const shouldLinkOperational =
      enumKey &&
      LINKABLE_OPERATIONAL_MANAGEMENT_STATUS_KEYS.includes(
        enumKey as VEHICLE_STATUS
      );
    const shouldLinkVehicles =
      enumKey &&
      LINKABLE_VEHICLES_STATUS_KEYS.includes(enumKey as VEHICLE_STATUS);

    let statusLink: string | null = null;
    statusLink = shouldLinkOperational
      ? `${window.location.origin}/operational-management?status=${enumKey}`
      : shouldLinkVehicles
      ? `${window.location.origin}/veiculos?status=${enumKey}`
      : null;

    if (enumKey === "MAINTENANCE_DISPUTED") {
      statusLink = `${window.location.origin}/relatorios/contestacoes`;
    } else if (enumKey === "MAINTENANCE_NOT_COMPLETED") {
      statusLink = `${window.location.origin}/manutencao`;
    }

    const deadline = DEADLINE_STATUS[enumKey as VEHICLE_STATUS] || 0;

    let ArrowIcon: React.ComponentType<any> | null = null;
    let arrowColor = "text-gray";

    if (delta === 0) {
      ArrowIcon = FaEquals;
      arrowColor = "w-6 h-6 text-slate-400";
    } else if (delta > 0) {
      ArrowIcon = ArrowBigDown;
      if (enumKey === "rented") {
        arrowColor = "w-12 h-12 text-red-600";
      } else {
        arrowColor = `w-12 h-12 text-green-600 ${
          delayed ? "animate-pulse-arrow-green" : ""
        }`;
      }
    } else if (delta < 0) {
      ArrowIcon = ArrowBigUp;
      if (enumKey === "rented") {
        arrowColor = "w-12 h-12 text-green-600";
      } else {
        arrowColor = `w-12 h-12 text-red-600 ${
          delayed ? "animate-pulse-arrow-red" : ""
        }`;
      }
    }

    const isOperationalStatus = (key: string): key is VEHICLE_STATUS =>
      LINKABLE_OPERATIONAL_MANAGEMENT_STATUS_KEYS.includes(
        key as VEHICLE_STATUS
      );

    const isVehiclesStatus = (key: string): key is VEHICLE_STATUS =>
      LINKABLE_VEHICLES_STATUS_KEYS.includes(key as VEHICLE_STATUS);

    const buildStatusPath = (key: string, delayed = false): string | null => {
      if (!key) return null;

      if (key === "repossession") return "/recolhimento";
      if (key === "MAINTENANCE_DISPUTED") return "/relatorios/contestacoes";
      if (key === "MAINTENANCE_NOT_COMPLETED") return "/manutencao";

      const params = new URLSearchParams({ status: key });
      if (delayed) params.set("situation", "DELAYED");

      if (isVehiclesStatus(key)) return `/veiculos?${params.toString()}`;
      if (isOperationalStatus(key))
        return `/operational-management?${params.toString()}`;

      return null;
    };

    const handleClickStatus = (delayed = false) => {
      if (!enumKey) return;

      const path = buildStatusPath(enumKey, delayed);
      if (!path) return;

      if (typeof window === "undefined" || typeof navigator === "undefined") {
        navigate(path);
        return;
      }

      const isMobile =
        (navigator as any).userAgentData?.mobile ??
        /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

      if (isMobile) {
        navigate(path);
      } else {
        window.open(path, "_blank", "noopener,noreferrer");
      }
    };

    return (
      <div
        className={`relative bg-white border border-gray5 shadow-md rounded-2xl p-5 transition hover:shadow-lg hover:scale-[1.01] ${className} ${
          statusLink ? "cursor-pointer" : ""
        } ${delayed ? "animate-pulse font-semibold animate-pulse-color" : ""}`}
        id={id}
      >
        {deadline > 0 && (
          <div className="absolute top-2 left-2">
            <span className="bg-gray-500 text-white text-xs font-bold px-2 py-1 rounded">
              {deadline}
            </span>
          </div>
        )}

        <div className="grid grid-cols-[1fr_auto] gap-4 items-center justify-center">
          <div className="flex flex-col justify-center items-center">
            <div className="mb-2 flex items-center gap-2">
              {icon}
              <h3 className={`text-xl font-semibold`}>{title}</h3>
            </div>

            {statusLink ? (
              <a
                onClick={() => handleClickStatus(false)}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline hover:text-blue-500"
              >
                <p className={`text-3xl font-semibold`}>{displayValue}</p>
              </a>
            ) : (
              <p className={`text-3xl font-semibold`}>{displayValue}</p>
            )}
          </div>

          {(shouldLinkOperational || shouldLinkVehicles) && (
            <div className="flex flex-col justify-between items-end h-full gap-2 items-center">
              {showDeltaArrow && ArrowIcon && (
                <ArrowIcon
                  className={`${arrowColor} fill-current stroke-none`}
                />
              )}
              {totalDelayed > 0 && (
                <a
                  onClick={() => handleClickStatus(true)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline hover:text-blue-500"
                >
                  <span className="bg-white text-black text-xs font-bold px-2 py-0.5 rounded-full shadow animate-ping-fast">
                    {totalDelayed}
                  </span>
                </a>
              )}
            </div>
          )}
        </div>
        {percentage > 0 && (
          <div className="absolute bottom-2 left-2">
            <span
              className={`bg-gray-500 ${
                delayed ? "text-white" : "text-black"
              } text-xs font-bold px-2 py-1 rounded`}
            >
              {percentage.toFixed(2)}%
            </span>
          </div>
        )}
      </div>
    );
  }
}
